import React, { Fragment } from 'react'
import style from './button.mod.scss'
import { useStaticQuery, graphql, Link } from 'gatsby'
import PropTypes from 'prop-types'

// Add the Icons object before ButtonContent
const Icons = {
  person: (
    <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18">
      <path
        d="M1.5 18S0 18 0 16.5s1.5-6 9-6 9 4.5 9 6-1.5 1.5-1.5 1.5h-15ZM9 9a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Z"
        fill="#00A098"
      />
    </svg>
  )
}

const ButtonContent = ({ children, icon }) => (
  <Fragment>
    {icon && <span className={style.button__icon}>{Icons[icon]}</span>}
    <span className={style.button__text}>{children}</span>
  </Fragment>
)

const Button = ({
  activeClass = '',
  alignment = 'left',
  buttonStyle = 'primary',
  children,
  color = 'default',
  disabled = false,
  elevated = false,
  gaTarget,
  icon = null,
  isMenuItem = false,
  modifiers = [],
  onClick,
  size = 'medium',
  length = 'medium',
  title,
  to = '/',
  type = null,
  unstyled = false,
  ...props
}) => {
  const data = useStaticQuery(graphql`
    query SiteUrlQuery {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `)

  // Align the button
  alignment &&
    modifiers.push(
      `${style.button}--alignment-${alignment
        .toLowerCase()
        .replace(/ /gi, '-')}`
    )
  // Sets the main color of the button - background/outline/text
  modifiers.push(
    `${style.button}--color-${color.toLowerCase().replace(/ /gi, '-')}`
  )
  // Sets the vertical padding
  modifiers.push(
    `${style.button}--size-${size.toLowerCase().replace(/ /gi, '-')}`
  )

  // Sets the horizontal padding
  modifiers.push(
    `${style.button}--length-${length.toLowerCase().replace(/ /gi, '-')}`
  )
  // If elevated is true this will add a shadow
  elevated && modifiers.push(`${style.button}--elevated`)
  // Sets the button style
  modifiers.push(
    `${style.button}--style-${buttonStyle.toLowerCase().replace(/ /gi, '-')}`
  )
  // Used in menu item component
  isMenuItem && modifiers.push(`${style.button}--menu-item`)

  const { siteUrl } = data.site.siteMetadata

  const externalLink =
    to.search(/^tel:/g) > -1 ||
    to.search(/^mailto:/g) > -1 ||
    (to.search(/http/g) > -1 && to.search(siteUrl) === -1)

  const config = {
    className: unstyled
      ? [style['button--unstyled'], ...modifiers].join(' ')
      : [style.button, ...modifiers].join(' '),
    onClick
  }

  // Strip the siteURL from the to field to create a relative path for <Link />
  if (!externalLink && !type && to.search(siteUrl) === -1)
    to.replace(siteUrl, '')

  // Set activeClassName for <Link />
  if (!externalLink && !type && activeClass)
    config.activeClassName = style[`button--${activeClass}`]

  // Set ariaLabel for <button />
  if (title && type) config.ariaLabel = title

  // Set title for <a /> and <Link />
  if (title && !type) config.title = title

  if (disabled) config.disabled = 'disabled'
  if (gaTarget) config['data-ga-target'] = gaTarget

  if (externalLink) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" {...config}>
        <ButtonContent children={children} icon={icon} />
      </a>
    )
  } else if (type) {
    return (
      <button type={type} {...config}>
        <ButtonContent children={children} icon={icon} />
      </button>
    )
  } else {
    return (
      <Link to={to} {...config} {...props}>
        <ButtonContent children={children} icon={icon} />
      </Link>
    )
  }
}

export default Button

Button.propTypes = {
  to: PropTypes.string,
  children: PropTypes.node.isRequired,
  type: PropTypes.string,
  onClick: PropTypes.func,
  size: PropTypes.string,
  modifiers: PropTypes.array,
  icon: PropTypes.string
}

export const query = graphql`
  fragment Link on DatoCmsLink {
    id
    displayText
    url
    link {
      ... on DatoCmsHome {
        id
        path
      }
      ... on DatoCmsPage {
        id
        path
      }
    }
    __typename
  }

  fragment Button on DatoCmsButton {
    id
    paddingTop
    paddingBottom
    alignment
    background
    colour
    model {
      apiKey
    }
    link {
      ...Link
    }
    __typename
  }

  fragment BookingButton on DatoCmsBookingButton {
    id
    paddingTop
    paddingBottom
    background
    displayText
    buttonAlignment
    link {
      url: link
    }
    model {
      apiKey
    }
    __typename
  }
`
